import { observable, toJS } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";

import RolePermissionListView, { PermissionSummary } from "./role-permission-list.view";

interface IRolePermissionControllerProps {
	mode: ControlMode;
	role: RoleItem;
	permissions: Array<PermissionItem>;
}

interface IRolePermissionControllerState {
	data: Array<PermissionSummary>;
}

@observer
export default class RolePermissionController extends React.Component<IRolePermissionControllerProps, IRolePermissionControllerState> {

	public componentDidMount(): void {
		const { permissions, role } = this.props;

		const rolePermissions = role.dynamicProperties.permissions;
		this.loadHierarchy(rolePermissions, permissions);
	}

	public componentWillReceiveProps(nextProps: Readonly<IRolePermissionControllerProps>) {
		const { permissions, role } = nextProps;

		if (this.props.permissions.length !== permissions.length || this.props.role.id !== role.id) {
			const rolePermissions = role.dynamicProperties.permissions;
			this.loadHierarchy(rolePermissions, permissions);
		}
	}

	public render(): React.ReactNode {
		return <RolePermissionListView data={this.state != null ? this.state.data : []} mode={this.props.mode} onRoleChange={this.onRoleChange} />;
	}

	private loadHierarchy(rolePermissions: Array<number>, permissions: Array<PermissionItem>) {
		let data = [];
		for (const item of permissions) {
			const isChecked = rolePermissions == null ? false : rolePermissions.some((e) => e === item.id);
			const newItem = observable({
				...toJS(item),
				isChecked,
				children: null,
			});
			data.push(newItem);
		}
		data = this.treeify(data);

		this.setState({ data });
	}

	treeify(list, idAttr?: string, parentAttr?: string, childrenAttr?: string) {
		if (!idAttr) idAttr = 'id';
		if (!parentAttr) parentAttr = 'parentId';
		if (!childrenAttr) childrenAttr = 'children';

		const treeList: any[] = [];
		const lookup: any = {};
		for (const obj of list) {
			lookup[obj[idAttr]] = obj;
		}
		for (const obj of list) {
			if (obj[parentAttr] != null) {
				if (lookup[obj[parentAttr]][childrenAttr] == null) {
					lookup[obj[parentAttr]][childrenAttr] = [];
				}
				lookup[obj[parentAttr]][childrenAttr].push(obj);
			} else {
				treeList.push(obj);
			}
		}
		return treeList;
	}

	private onRoleChange = (permissionId: number, isChecked: boolean, reload: boolean = true) => {
		let permissions: Array<number> = this.props.role.dynamicProperties.permissions;
		if (permissions == null) {
			permissions = [];
			this.props.role.dynamicProperties.permissions = permissions;
		}

		let index = permissions.findIndex(p => p === permissionId);
		if (index === -1 && isChecked) {
			permissions.push(permissionId);
		} else if (index !== -1 && !isChecked) {
			permissions.splice(index, 1);
		}
		const item = this.findItem(permissionId);
		if (item) {
			if (item.children) {
				for (const child of item.children) {
					this.onRoleChange(child.id, isChecked, false);
				}
			}
			this.onRoleChangeUp(item, isChecked, permissions);
		}
		if (reload) {
			this.loadHierarchy(permissions, this.props.permissions);
		}
	}

	private onRoleChangeUp(item: PermissionSummary, isChecked: boolean, permissions: Array<number>) {
		if (item.parentId) {
			const parent = this.findItem(item.parentId);
			let index = permissions.findIndex(p => p === item.parentId);
			if (isChecked && parent.children.every(c => c.id === item.id || c.isChecked)) {
				if (permissions.indexOf(item.parentId) === -1) {
					permissions.push(item.parentId);
					this.onRoleChangeUp(parent, isChecked, permissions);
				}
			} else if (!isChecked && parent.isChecked) {
				permissions.splice(index, 1);
				this.onRoleChangeUp(parent, isChecked, permissions)
			}
		}
	}

	private findItem(permissionId: number): PermissionSummary {
		return this.findInChildren(this.state.data, permissionId);
	}

	private findInChildren(array: Array<PermissionSummary>, permissionId: number): PermissionSummary {
		if (array) {
			for (const i of array) {
				const result = i.id === permissionId ? i : this.findInChildren(i.children, permissionId);
				if (result) {
					return result;
				}
			}
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/general/roles/form/parts/role-permission-list.controller.tsx